import type { FC } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useJackpotStatisticsStore } from '../../store/jackpotStatisticsStore';
import JackpotStatisticsDrawer from './JackpotStatisticsDrawer';

interface JackpotStatisticsHostProps {
  /** True while a game launch is already in flight — forwarded to the drawer's launch guard. */
  isLaunchBlocked: boolean;
  /**
   * The games feature's launch action. Injected by the layout because the games feature already
   * consumes jackpot data — importing it from here would close an import cycle.
   */
  onStartGame: (gameKey: string) => void;
}

/**
 * Single mount point for the jackpot statistics drawer.
 *
 * The drawer subtree is unmounted while closed so its react-query fetch and its filter state
 * reset between openings, and so nothing renders above the lobby when it is not in use.
 */
const JackpotStatisticsHost: FC<JackpotStatisticsHostProps> = ({ isLaunchBlocked, onStartGame }) => {
  const { isOpen, jackpotName, sectionName, close } = useJackpotStatisticsStore(
    useShallow((state) => ({
      isOpen: state.isOpen,
      jackpotName: state.jackpotName,
      sectionName: state.sectionName,
      close: state.close,
    })),
  );

  if (!isOpen) return null;

  return (
    <JackpotStatisticsDrawer
      jackpotName={jackpotName}
      sectionName={sectionName}
      isLaunchBlocked={isLaunchBlocked}
      onStartGame={onStartGame}
      onClose={close}
    />
  );
};

export default JackpotStatisticsHost;
